import {useEffect, useMemo, useState} from "react";
import {httpsCallable} from 'firebase/functions';

import {database, functions} from "hooks/firebase";
import {BrandTraitScoresResponse, RankingBrandTraitsRequest} from "data/models/shared/ranked-brand-traits";
import {useObjectVal} from "react-firebase-hooks/database";
import {ref} from "firebase/database";
import {useAuthState} from "react-firebase-hooks/auth";
import {getAuth} from "firebase/auth";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {MultiSelectValue} from "data/models/survey/multi-choice";
import {ChoiceValue} from "data/models/survey/choice-base";
import {multiSelectValues} from "helpers/choice-values";

export function useRankedBrandTraits(): [BrandTraitScoresResponse | null, boolean, Error | null] {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const canvaAssetsPath = useMemo(() => `user-data/${user?.uid ?? ""}/user-surveys/canva-assets/all-features`, [user]);
  const uniqueTraitsPath = useMemo(() => `${canvaAssetsPath}/what-makes-you-special`, [canvaAssetsPath]);
  const [productDescriptionWords, productDescriptionWordsLoading, productDescriptionWordsError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${uniqueTraitsPath}/product-description-words`)) ?? null);
  const [serviceExperience, serviceExperienceLoading, serviceExperienceError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${uniqueTraitsPath}/service-description-words`)) ?? null);
  const [locationAtmosphere, locationAtmosphereLoading, locationAtmosphereError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${uniqueTraitsPath}/location-description-words`)) ?? null);
  const [feelsLikeToUse, feelsLikeToUseLoading, feelsLikeToUseError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${uniqueTraitsPath}/digital-description-words`)) ?? null);
  const [pictureOfYourBrand, pictureOfYourBrandLoading, pictureOfYourBrandError] =
      useObjectVal<MultiSelectValue>((user && ref(database, `${uniqueTraitsPath}/picture-of-your-brand`)) ?? null);
  const [rankBenefitsAndTraits, rankBenefitsAndTraitsLoading, rankBenefitsAndTraitsError] =
      useObjectVal<ChoiceValue[]>((user && ref(database, `${uniqueTraitsPath}/rank-benefits-and-traits`)) ?? null);

  const brandVisualStylesPath = useMemo(() => `${canvaAssetsPath}/visual-recommendations/visualize-your-brand`, [canvaAssetsPath]);
  const [brandVisualStyles, brandVisualStylesLoading, brandVisualStylesError] =
      useObjectVal<string[]>((user && ref(database, brandVisualStylesPath)) ?? null);

  const [rankedBrandTraits, setRankedBrandTraits] = useState<BrandTraitScoresResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const anyLoading = (
      userLoading
      || productDescriptionWordsLoading
      || serviceExperienceLoading
      || locationAtmosphereLoading
      || feelsLikeToUseLoading
      || pictureOfYourBrandLoading
      || rankBenefitsAndTraitsLoading
      || brandVisualStylesLoading
  );
  const anError = (
      userError
      ?? productDescriptionWordsError
      ?? serviceExperienceError
      ?? locationAtmosphereError
      ?? feelsLikeToUseError
      ?? pictureOfYourBrandError
      ?? rankBenefitsAndTraitsError
      ?? brandVisualStylesError
  );

  useEffect(() => {
    if (!isNullOrUndefined(anError)) {
      setError(anError);
      return;
    }
    if (anyLoading) {
      return;
    }
    if (
        isNullOrUndefined(productDescriptionWords)
        || isNullOrUndefined(serviceExperience)
        || isNullOrUndefined(locationAtmosphere)
        || isNullOrUndefined(feelsLikeToUse)
        || isNullOrUndefined(pictureOfYourBrand)
        || isNullOrUndefined(rankBenefitsAndTraits)
        || isNullOrUndefined(brandVisualStyles)
    ) {
      return;
    }

    const brandTraitsRequest: RankingBrandTraitsRequest = {
      brandCharacteristics: [
        ...(multiSelectValues(productDescriptionWords) ?? []),
        ...(multiSelectValues(serviceExperience) ?? []),
        ...(multiSelectValues(locationAtmosphere) ?? []),
        ...(multiSelectValues(feelsLikeToUse) ?? []),
        ...(multiSelectValues(pictureOfYourBrand) ?? []),
      ],
      brandGraphicsRepresentations: brandVisualStyles,
      userBoostedTraits: rankBenefitsAndTraits.map(ranked => ranked.key),
    };

    const brandTraitScoresFunction = httpsCallable<RankingBrandTraitsRequest, BrandTraitScoresResponse>(functions, "brandTraitScores");

    brandTraitScoresFunction(brandTraitsRequest).then(result => {
      setRankedBrandTraits(result.data);
      setError(null);
    }).catch((error) => {
      setError(error as Error);
    });
  }, [
    anyLoading, anError,
    productDescriptionWords,
    serviceExperience,
    locationAtmosphere,
    feelsLikeToUse,
    pictureOfYourBrand,
    rankBenefitsAndTraits,
    brandVisualStyles,
  ]);

  return [rankedBrandTraits, anyLoading, error];
}
